import React, { useEffect, useState } from "react";
import { motion } from "framer-motion";
import { FaGraduationCap, FaUsers, FaClock, FaStar } from "react-icons/fa";
import daycare from "../assets/daycare.png";
import playgroup from "../assets/playgroup.png";
import nursery from "../assets/nursery.png";
import junior from "../assets/junior.png";
import senior from "../assets/senior.png";

const CourseSection = () => {
  const [isVisible, setIsVisible] = useState(false);

  useEffect(() => {
    const observer = new IntersectionObserver(
      ([entry]) => {
        if (entry.isIntersecting) {
          setIsVisible(true);
        }
      },
      { threshold: 0.1 }
    );

    const element = document.getElementById('courses-section');
    if (element) observer.observe(element);

    return () => observer.disconnect();
  }, []);

  const courses = [
    {
      id: 1,
      title: "Bagless Homeschooling",
      subtitle: "Nursery, Jr.KG, Sr.KG",
      image: daycare,
      ageGroup: "3 to 5 years",
      duration: "2 hours",
      students: "10-15",
      features: ["Play-based Learning", "Creative Activities", "Individual Attention"],
      color: "from-pink-400 to-red-400",
      bgColor: "bg-gradient-to-br from-pink-50 to-red-50",
      iconColor: "text-pink-500"
    },
    {
      id: 2,
      title: "Language Expert Classes",
      subtitle: "English, Marathi, Hindi",
      image: playgroup,
      ageGroup: "No Age Limit",
      duration: "Flexible",
      students: "5-10",
      features: ["Multi-language Support", "Expert Teachers", "Interactive Sessions"],
      color: "from-purple-400 to-pink-400",
      bgColor: "bg-gradient-to-br from-purple-50 to-pink-50",
      iconColor: "text-purple-500"
    }
  ];

  const containerVariants = {
    hidden: { opacity: 0 },
    visible: {
      opacity: 1,
      transition: {
        staggerChildren: 0.3
      }
    }
  };

  const cardVariants = {
    hidden: {
      opacity: 0,
      y: 50,
      scale: 0.9
    },
    visible: {
      opacity: 1,
      y: 0,
      scale: 1,
      transition: {
        duration: 0.6,
        ease: "easeOut"
      }
    }
  };

  return (
    <section id="courses-section" className="py-20 bg-gradient-to-br from-gray-50 via-white to-blue-50 relative overflow-hidden">
      {/* Background Decorations */}
      <div className="absolute top-0 left-0 w-72 h-72 bg-primary-100/30 rounded-full blur-3xl -translate-x-1/2 -translate-y-1/2"></div>
      <div className="absolute bottom-0 right-0 w-96 h-96 bg-secondary-100/20 rounded-full blur-3xl translate-x-1/2 translate-y-1/2"></div>

      <div className="container mx-auto px-6 lg:px-12 relative z-10">
        {/* Section Header */}
        <motion.div
          className="text-center mb-16"
          initial={{ opacity: 0, y: 30 }}
          animate={isVisible ? { opacity: 1, y: 0 } : {}}
          transition={{ duration: 0.8 }}
        >
          <motion.div
            className="inline-flex items-center space-x-2 bg-primary-100 text-primary-700 px-6 py-2 rounded-full text-sm font-semibold mb-4"
            initial={{ opacity: 0, scale: 0.8 }}
            animate={isVisible ? { opacity: 1, scale: 1 } : {}}
            transition={{ duration: 0.6, delay: 0.2 }}
          >
            <FaGraduationCap />
            <span>Our Programs</span>
          </motion.div>

          <h2 className="text-4xl lg:text-6xl font-bold font-platypi text-gray-800 mb-6">
            <span className="gradient-text bg-gradient-to-r from-primary-600 to-secondary-600 bg-clip-text text-transparent">
              Educational
            </span>
            <br />
            <span className="text-gray-700">Excellence</span>
          </h2>

          <p className="text-xl text-gray-600 max-w-3xl mx-auto leading-relaxed">
            Discover our innovative learning programs designed to nurture young minds
            and create a foundation for lifelong success.
          </p>
        </motion.div>

        {/* Courses Grid */}
        <motion.div
          className="grid lg:grid-cols-2 gap-8 lg:gap-12"
          variants={containerVariants}
          initial="hidden"
          animate={isVisible ? "visible" : "hidden"}
        >
          {courses.map((course, index) => (
            <motion.div
              key={course.id}
              variants={cardVariants}
              className="group h-full"
            >
              <div className="relative bg-white rounded-3xl p-8 h-full shadow-xl hover:shadow-2xl transition-all duration-500 group-hover:-translate-y-3 border border-gray-100 overflow-hidden">
                {/* Gradient Background */}
                <div className={`absolute inset-0 bg-gradient-to-br ${course.color} opacity-0 group-hover:opacity-5 transition-opacity duration-500`}></div>

                {/* Decorative Elements */}
                <div className="absolute -top-12 -right-12 w-40 h-40 bg-gradient-to-br from-purple-400/15 to-pink-400/15 rounded-full blur-2xl group-hover:scale-125 transition-transform duration-700"></div>
                <div className="absolute -bottom-8 -left-8 w-32 h-32 bg-gradient-to-br from-blue-400/15 to-cyan-400/15 rounded-full blur-xl group-hover:scale-110 transition-transform duration-700"></div>

                {/* Course Image with Enhanced Design */}
                <div className="relative z-10 flex justify-center mb-8">
                  <div className="relative">
                    {/* Multiple Glow Effects */}
                    <div className={`absolute inset-0 bg-gradient-to-br ${course.color} rounded-full blur-2xl opacity-20 group-hover:opacity-40 transition-opacity duration-500`}></div>
                    <div className="absolute inset-0 bg-white/30 rounded-full blur-xl group-hover:blur-2xl transition-all duration-500"></div>

                    {/* Image Container */}
                    <motion.div
                      className="relative bg-gradient-to-br from-white to-gray-50 rounded-full p-6 shadow-lg group-hover:shadow-xl transition-shadow duration-300"
                      whileHover={{ scale: 1.05, rotate: [0, -3, 3, 0] }}
                      transition={{ duration: 0.5 }}
                    >
                      <img
                        src={course.image}
                        alt={course.title}
                        className="relative z-10 w-32 h-32 object-contain drop-shadow-lg"
                      />

                      {/* Floating Badge */}
                      <div className={`absolute -top-2 -right-2 w-8 h-8 bg-gradient-to-br ${course.color} rounded-full flex items-center justify-center text-white text-xs font-bold shadow-lg animate-pulse`}>
                        ⭐
                      </div>
                    </motion.div>
                  </div>
                </div>

                {/* Course Info */}
                <div className="relative z-10 text-center space-y-6">
                  {/* Header Section */}
                  <div className="space-y-4">
                    {/* Age Badge */}
                    <motion.div
                      className={`inline-flex items-center bg-gradient-to-r ${course.color} text-white px-6 py-3 rounded-full text-sm font-bold shadow-lg hover:shadow-xl transition-shadow duration-300`}
                      whileHover={{ scale: 1.05 }}
                      transition={{ duration: 0.2 }}
                    >
                      <div className="w-5 h-5 bg-white/20 rounded-full flex items-center justify-center mr-2">
                        <FaClock size={12} />
                      </div>
                      {course.ageGroup}
                    </motion.div>

                    {/* Title */}
                    <div>
                      <h3 className="text-3xl font-bold font-platypi text-gray-800 mb-3 group-hover:text-gray-900 transition-colors">
                        {course.title}
                      </h3>
                      <p className="text-lg text-gray-600 font-medium leading-relaxed">
                        {course.subtitle}
                      </p>
                    </div>
                  </div>

                  {/* Enhanced Stats */}
                  <div className="grid grid-cols-3 gap-4 py-6">
                    <motion.div
                      className="text-center p-4 bg-gradient-to-br from-gray-50 to-gray-100 rounded-2xl border border-gray-200 group-hover:border-gray-300 transition-all duration-300"
                      whileHover={{ scale: 1.05, y: -2 }}
                      transition={{ duration: 0.2 }}
                    >
                      <div className={`w-12 h-12 mx-auto mb-3 bg-gradient-to-br ${course.color} rounded-xl flex items-center justify-center text-white shadow-md`}>
                        <FaUsers size={20} />
                      </div>
                      <div className="text-sm font-bold text-gray-800 mb-1">{course.students}</div>
                      <div className="text-xs text-gray-500 font-medium">Students</div>
                    </motion.div>

                    <motion.div
                      className="text-center p-4 bg-gradient-to-br from-gray-50 to-gray-100 rounded-2xl border border-gray-200 group-hover:border-gray-300 transition-all duration-300"
                      whileHover={{ scale: 1.05, y: -2 }}
                      transition={{ duration: 0.2 }}
                    >
                      <div className={`w-12 h-12 mx-auto mb-3 bg-gradient-to-br ${course.color} rounded-xl flex items-center justify-center text-white shadow-md`}>
                        <FaClock size={20} />
                      </div>
                      <div className="text-sm font-bold text-gray-800 mb-1">{course.duration}</div>
                      <div className="text-xs text-gray-500 font-medium">Duration</div>
                    </motion.div>

                    <motion.div
                      className="text-center p-4 bg-gradient-to-br from-gray-50 to-gray-100 rounded-2xl border border-gray-200 group-hover:border-gray-300 transition-all duration-300"
                      whileHover={{ scale: 1.05, y: -2 }}
                      transition={{ duration: 0.2 }}
                    >
                      <div className={`w-12 h-12 mx-auto mb-3 bg-gradient-to-br ${course.color} rounded-xl flex items-center justify-center text-white shadow-md`}>
                        <FaStar size={20} />
                      </div>
                      <div className="text-sm font-bold text-gray-800 mb-1">5.0</div>
                      <div className="text-xs text-gray-500 font-medium">Rating</div>
                    </motion.div>
                  </div>

                  {/* Enhanced Features */}
                  <div className="space-y-3">
                    {course.features.map((feature, idx) => (
                      <motion.div
                        key={idx}
                        className="flex items-center justify-center space-x-3 p-3 bg-white/60 rounded-xl border border-gray-100 hover:border-gray-200 hover:bg-white/80 transition-all duration-300 group/feature"
                        initial={{ opacity: 0, y: 10 }}
                        animate={{ opacity: 1, y: 0 }}
                        transition={{ duration: 0.3, delay: idx * 0.1 }}
                        whileHover={{ scale: 1.02 }}
                      >
                        <div className={`w-6 h-6 bg-gradient-to-br ${course.color} rounded-full flex items-center justify-center shadow-sm`}>
                          <div className="w-2 h-2 bg-white rounded-full"></div>
                        </div>
                        <span className="text-sm font-semibold text-gray-700 group-hover/feature:text-gray-800 transition-colors">
                          {feature}
                        </span>
                        <div className="ml-auto">
                          <div className="w-2 h-2 bg-green-400 rounded-full animate-pulse"></div>
                        </div>
                      </motion.div>
                    ))}
                  </div>

                  {/* Enhanced CTA Button */}
                  <div className="space-y-4 pt-4">
                    <motion.a
                      href={`https://wa.me/919834387679?text=Hi! I want to enroll my child in *${course.title}* (${course.subtitle}) for age group ${course.ageGroup}. Please provide more details about the course curriculum, fees, schedule, and admission process. Thank you!`}
                      target="_blank"
                      rel="noopener noreferrer"
                      className={`w-full bg-gradient-to-r ${course.color} text-white py-4 px-6 rounded-2xl font-bold text-lg shadow-lg hover:shadow-xl transition-all duration-300 flex items-center justify-center space-x-3 group/btn block`}
                      whileHover={{ scale: 1.02, y: -2 }}
                      whileTap={{ scale: 0.98 }}
                    >
                      <span>Enroll Now</span>
                      <motion.div
                        className="w-6 h-6 bg-white/20 rounded-full flex items-center justify-center"
                        animate={{ x: [0, 4, 0] }}
                        transition={{ duration: 1.5, repeat: Infinity }}
                      >
                        <svg className="w-4 h-4" fill="currentColor" viewBox="0 0 24 24">
                          <path d="M17.472 14.382c-.297-.149-1.758-.867-2.03-.967-.273-.099-.471-.148-.67.15-.197.297-.767.966-.94 1.164-.173.199-.347.223-.644.075-.297-.15-1.255-.463-2.39-1.475-.883-.788-1.48-1.761-1.653-2.059-.173-.297-.018-.458.13-.606.134-.133.298-.347.446-.52.149-.174.198-.298.298-.497.099-.198.05-.371-.025-.52-.075-.149-.669-1.612-.916-2.207-.242-.579-.487-.5-.669-.51-.173-.008-.371-.01-.57-.01-.198 0-.52.074-.792.372-.272.297-1.04 1.016-1.04 2.479 0 1.462 1.065 2.875 1.213 3.074.149.198 2.096 3.2 5.077 4.487.709.306 1.262.489 1.694.625.712.227 1.36.195 1.871.118.571-.085 1.758-.719 2.006-1.413.248-.694.248-1.289.173-1.413-.074-.124-.272-.198-.57-.347m-5.421 7.403h-.004a9.87 9.87 0 01-5.031-1.378l-.361-.214-3.741.982.998-3.648-.235-.374a9.86 9.86 0 01-1.51-5.26c.001-5.45 4.436-9.884 9.888-9.884 2.64 0 5.122 1.03 6.988 2.898a9.825 9.825 0 012.893 6.994c-.003 5.45-4.437 9.884-9.885 9.884m8.413-18.297A11.815 11.815 0 0012.05 0C5.495 0 .16 5.335.157 11.892c0 2.096.547 4.142 1.588 5.945L.057 24l6.305-1.654a11.882 11.882 0 005.683 1.448h.005c6.554 0 11.89-5.335 11.893-11.893A11.821 11.821 0 0020.885 3.787"/>
                        </svg>
                      </motion.div>
                    </motion.a>

                    {/* Course Footer */}
                    <div className="flex items-center justify-between text-xs text-gray-500">
                      <div className="flex items-center space-x-1">
                        <div className="w-2 h-2 bg-green-400 rounded-full animate-pulse"></div>
                        <span>Enrollment Open</span>
                      </div>
                      <div className="flex items-center space-x-1">
                        <svg className="w-3 h-3" fill="currentColor" viewBox="0 0 20 20">
                          <path fillRule="evenodd" d="M3.172 5.172a4 4 0 015.656 0L10 6.343l1.172-1.171a4 4 0 115.656 5.656L10 17.657l-6.828-6.829a4 4 0 010-5.656z" clipRule="evenodd" />
                        </svg>
                        <span>Loved by parents</span>
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            </motion.div>
          ))}
        </motion.div>

        {/* Bottom CTA */}
        <motion.div
          className="text-center mt-16"
          initial={{ opacity: 0, y: 30 }}
          animate={isVisible ? { opacity: 1, y: 0 } : {}}
          transition={{ duration: 0.8, delay: 0.6 }}
        >
          <p className="text-lg text-gray-600 mb-6">
            Ready to give your child the best educational experience?
          </p>
          <motion.a
            href="https://wa.me/919834387679?text=Hi! I'm ready to give my child the best educational experience at Smart Educare Center. Please schedule a visit for me so I can see your innovative learning programs, meet the expert teachers, and understand how you create educational excellence. Let me know available dates and times for a center visit. Thank you!"
            target="_blank"
            rel="noopener noreferrer"
            className="bg-gradient-primary text-white px-8 py-4 rounded-2xl font-semibold text-lg shadow-medium hover:shadow-strong transition-all duration-300 inline-flex items-center space-x-3"
            whileHover={{ scale: 1.05 }}
            whileTap={{ scale: 0.95 }}
          >
            <svg className="w-6 h-6" fill="currentColor" viewBox="0 0 24 24">
              <path d="M17.472 14.382c-.297-.149-1.758-.867-2.03-.967-.273-.099-.471-.148-.67.15-.197.297-.767.966-.94 1.164-.173.199-.347.223-.644.075-.297-.15-1.255-.463-2.39-1.475-.883-.788-1.48-1.761-1.653-2.059-.173-.297-.018-.458.13-.606.134-.133.298-.347.446-.52.149-.174.198-.298.298-.497.099-.198.05-.371-.025-.52-.075-.149-.669-1.612-.916-2.207-.242-.579-.487-.5-.669-.51-.173-.008-.371-.01-.57-.01-.198 0-.52.074-.792.372-.272.297-1.04 1.016-1.04 2.479 0 1.462 1.065 2.875 1.213 3.074.149.198 2.096 3.2 5.077 4.487.709.306 1.262.489 1.694.625.712.227 1.36.195 1.871.118.571-.085 1.758-.719 2.006-1.413.248-.694.248-1.289.173-1.413-.074-.124-.272-.198-.57-.347m-5.421 7.403h-.004a9.87 9.87 0 01-5.031-1.378l-.361-.214-3.741.982.998-3.648-.235-.374a9.86 9.86 0 01-1.51-5.26c.001-5.45 4.436-9.884 9.888-9.884 2.64 0 5.122 1.03 6.988 2.898a9.825 9.825 0 012.893 6.994c-.003 5.45-4.437 9.884-9.885 9.884m8.413-18.297A11.815 11.815 0 0012.05 0C5.495 0 .16 5.335.157 11.892c0 2.096.547 4.142 1.588 5.945L.057 24l6.305-1.654a11.882 11.882 0 005.683 1.448h.005c6.554 0 11.89-5.335 11.893-11.893A11.821 11.821 0 0020.885 3.787"/>
            </svg>
            <span>Schedule a Visit</span>
            <motion.div
              className="w-6 h-6 bg-white/20 rounded-full flex items-center justify-center"
              animate={{ x: [0, 4, 0] }}
              transition={{ duration: 1.5, repeat: Infinity }}
            >
              <svg className="w-3 h-3" fill="currentColor" viewBox="0 0 20 20">
                <path fillRule="evenodd" d="M10.293 3.293a1 1 0 011.414 0l6 6a1 1 0 010 1.414l-6 6a1 1 0 01-1.414-1.414L14.586 11H3a1 1 0 110-2h11.586l-4.293-4.293a1 1 0 010-1.414z" clipRule="evenodd" />
              </svg>
            </motion.div>
          </motion.a>
        </motion.div>
      </div>
    </section>
  );
};

export default CourseSection;
